export type DiffLineType = 'remove' | 'add' | 'context' | 'omit';

export interface DiffLine {
  type: DiffLineType;
  line: string;
  /** 1-based line in the pre-image (old file); set for remove/context from unified patches. */
  oldLineNo?: number | null;
  /** 1-based line in the post-image (new file); set for add/context from unified patches. */
  newLineNo?: number | null;
  /** Git marker state for a changed line that does not end with a final newline. */
  noNewlineAtEnd?: boolean;
  /** Synthetic row used to render an EOF newline-only addition/removal. */
  eofNewlineChange?: 'added' | 'removed';
}

export type EofNewlineChange = NonNullable<DiffLine['eofNewlineChange']>;

export function normalizeDiffLineText(line: string): string {
  return line.replace(/\r$/, '').replace(/\s*\\ No newline at end of file\s*$/, '');
}

export function normalizePatchContent(patch: string): string {
  return patch.replace(/^\n+/, '').replace(/\n+$/, '');
}

/**
 * Split file text into logical lines.
 * Drops the trailing empty segment from a final newline so EOF newline-only edits
 * are represented as a synthetic row rather than a blank content line.
 */
export function splitTextIntoDiffLines(text: string): string[] {
  if (text.length === 0) return [];
  const lines = text.split('\n').map((line) => normalizeDiffLineText(line));
  if (text.endsWith('\n') && lines.at(-1) === '') {
    lines.pop();
  }
  return lines;
}

export function createEofNewlineDiffLine(change: EofNewlineChange): DiffLine {
  return {
    type: change === 'added' ? 'add' : 'remove',
    line: '',
    oldLineNo: null,
    newLineNo: null,
    eofNewlineChange: change,
  };
}

function hasSameLogicalLines(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((line, index) => line === right[index]);
}

export function detectEofNewlineChange(
  oldText: string,
  newText: string,
  oldLines: string[],
  newLines: string[]
): EofNewlineChange | null {
  if (!hasSameLogicalLines(oldLines, newLines) || oldLines.length === 0) return null;
  if (!oldText.endsWith('\n') && newText.endsWith('\n')) return 'added';
  if (oldText.endsWith('\n') && !newText.endsWith('\n')) return 'removed';
  return null;
}

/**
 * Compute a line diff using LCS. Returns lines in order: removed, added, and context.
 */
export function computeLineDiff(oldText: string, newText: string): DiffLine[] {
  const oldLines = splitTextIntoDiffLines(oldText);
  const newLines = splitTextIntoDiffLines(newText);
  const m = oldLines.length;
  const n = newLines.length;

  const dp: number[][] = Array(m + 1);
  for (let i = 0; i <= m; i++) dp[i] = Array(n + 1).fill(0);
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldLines[i - 1] === newLines[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  const result: DiffLine[] = [];
  let i = m;
  let j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
      result.unshift({ type: 'context', line: oldLines[i - 1]! });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i]![j - 1]! >= dp[i - 1]![j]!)) {
      result.unshift({ type: 'add', line: newLines[j - 1]! });
      j--;
    } else {
      result.unshift({ type: 'remove', line: oldLines[i - 1]! });
      i--;
    }
  }
  const eofNewlineChange = detectEofNewlineChange(oldText, newText, oldLines, newLines);
  if (eofNewlineChange) result.push(createEofNewlineDiffLine(eofNewlineChange));
  return result;
}

export function editSnippetToUnifiedDiff(filePath: string, oldText: string, newText: string): string {
  const lines = computeLineDiff(oldText, newText);
  const out: string[] = [`--- ${filePath}`, `+++ ${filePath}`];
  for (const d of lines) {
    if (d.eofNewlineChange) {
      const previous = out.at(-1);
      if (previous?.startsWith(' ')) {
        const line = previous.slice(1);
        out.pop();
        if (d.eofNewlineChange === 'added') {
          out.push(`-${line}`, '\\ No newline at end of file', `+${line}`);
        } else {
          out.push(`-${line}`, `+${line}`, '\\ No newline at end of file');
        }
        continue;
      }
    }
    if (d.type === 'add') out.push(`+${d.line}`);
    else if (d.type === 'remove') out.push(`-${d.line}`);
    else out.push(` ${d.line}`);
  }
  return out.join('\n');
}
